<template>
  <ContactsLayout>
    <div class="bg-white rounded-lg shadow p-6 w-full max-w-full overflow-hidden">
      <!-- Header with actions -->
      <div class="flex justify-between items-center px-2 py-4 mb-6 bg-gray-50 border-b rounded-lg">
        <div class="flex items-center gap-2">
          <Button
            icon="pi pi-arrow-left"
            class="p-button-text"
            @click="goBack"
          />
          <h2 class="text-xl font-semibold">
            {{ service.name || 'Service Details' }}
          </h2>
        </div>

        <!-- Action buttons -->
        <div
          v-if="type === 'view' && !isEditing"
          class="flex gap-2"
        >
          <Button
            label="Edit"
            @click="startEdit"
          />
          <Button
            label="Delete"
            class="p-button-danger"
            @click="triggerDelete"
          />
        </div>

        <!-- Save/Update buttons -->
        <div
          v-if="isEditing"
          class="flex gap-2"
        >
          <Button
            v-if="type === 'add'"
            label="Add"
            @click="saveChanges(service)"
          />
          <Button
            v-if="type === 'view'"
            label="Update"
            @click="updateItem(service)"
          />
          <Button
            label="Cancel"
            class="p-button-secondary"
            @click="cancelEdit"
          />
        </div>
      </div>

      <!-- Main content -->
      <div class="w-full">
        <Tabs
          value="0"
          class="w-full"
        >
          <TabList class="w-full flex border-b">
            <Tab value="0">
              Overview
            </Tab>
            <Tab value="2">
              Documents
            </Tab>
            <Tab value="1">
              Pricing
            </Tab>
            <Tab value="3">
              Providers
            </Tab>
          </TabList>
          <TabPanels class="w-full py-6">
            <TabPanel
              value="0"
              class="w-full"
            >
              <!-- Overview content -->
              <div class="grid grid-cols-1 md:grid-cols-12 gap-8 w-full">
                <!-- Left Column: Image (align to the left) -->
                <div class="md:col-span-3 flex flex-col items-start">
                  <div
                    class="image-container shadow-md rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center"
                  >
                    <Image
                      :src="serviceImageSrc"
                      alt="Service Image"
                      class="w-full h-full object-contain"
                      @error="handleImageError"
                    />
                  </div>

                  <!-- File input for uploading a new image, visible only when editing or adding -->
                  <FileUpload
                    v-if="isEditing"
                    mode="basic"
                    accept="image/*"
                    auto
                    choose-label="Upload Image"
                    class="mt-4 w-full"
                    @select="onImageUpload"
                  />
                </div>

                <!-- Right Column: Form Fields -->
                <div class="md:col-span-9 w-full">
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <!-- Service Name -->
                    <div class="p-2">
                      <label
                        for="name"
                        class="block text-gray-700 mb-2 font-medium"
                      >Service
                        Name</label>
                      <InputText
                        id="name"
                        v-model="service.name"
                        :disabled="!isEditing"
                        class="w-full"
                      />
                    </div>

                    <!-- Service Type -->
                    <div class="p-2">
                      <label
                        for="type"
                        class="block text-gray-700 mb-2 font-medium"
                      >Service
                        Type</label>
                      <InputText
                        id="type"
                        v-model="service.type"
                        :disabled="!isEditing"
                        class="w-full"
                      />
                    </div>

                    <!-- Price Per Hour -->
                    <div class="p-2">
                      <label
                        for="pricePerHour"
                        class="block text-gray-700 mb-2 font-medium"
                      >Price
                        Per Hour</label>
                      <InputText
                        id="pricePerHour"
                        v-model="service.pricePerHour"
                        :disabled="!isEditing"
                        class="w-full"
                      />
                    </div>

                    <!-- Minimum Hours -->
                    <div class="p-2">
                      <label
                        for="minimumHours"
                        class="block text-gray-700 mb-2 font-medium"
                      >Minimum Hours</label>
                      <InputText
                        id="minimumHours"
                        v-model="service.minimumHours"
                        :disabled="!isEditing"
                        class="w-full"
                      />
                    </div>

                    <!-- Description spans full width -->
                    <div class="p-2 md:col-span-2">
                      <label
                        for="description"
                        class="block text-gray-700 mb-2 font-medium"
                      >Description</label>
                      <TextArea
                        id="description"
                        v-model="service.description"
                        rows="4"
                        :disabled="!isEditing"
                        class="w-full"
                      />
                    </div>

                    <!-- Bookable and VAT Checkboxes -->
                    <div class="flex items-center p-2">
                      <Checkbox
                        v-model="service.isBookable"
                        :binary="true"
                        :disabled="!isEditing"
                      />
                      <label class="ml-2 text-gray-700">Bookable</label>
                    </div>
                    <div class="flex items-center p-2">
                      <Checkbox
                        v-model="service.isVat"
                        :binary="true"
                        :disabled="!isEditing"
                      />
                      <label class="ml-2 text-gray-700">VAT Applicable</label>
                    </div>
                  </div>
                </div>
              </div>
            </TabPanel>
            <TabPanel
              value="1"
              class="w-full"
            >
              <div class="flex justify-between mb-4">
                <h3 class="text-lg font-medium">
                  Pricing Details
                </h3>
                <Button
                  v-if="isEditing"
                  icon="pi pi-plus"
                  label="Add Price Option"
                />
              </div>
              <DataTable
                :value="service.pricing || []"
                sort-field="name"
                scrollable
                scroll-height="500px"
                class="w-full"
              >
                <template #empty>
                  <div class="flex flex-wrap items-center justify-center py-8">
                    <div class="text-center">
                      <i class="pi pi-dollar text-5xl text-gray-300 mb-3" />
                      <p class="text-gray-500">
                        No pricing options have been added yet.
                      </p>
                    </div>
                  </div>
                </template>

                <!-- Columns -->
                <Column
                  field="name"
                  header="Option Name"
                  sortable
                />
                <Column
                  field="price"
                  header="Price"
                  sortable
                />
                <Column
                  field="unit"
                  header="Unit"
                  sortable
                />
                <Column
                  field="description"
                  header="Description"
                />
                <Column>
                  <template #body="slotProps">
                    <Button
                      icon="pi pi-times"
                      severity="danger"
                      rounded
                      outlined
                      aria-label="Delete"
                      @click="deleteItem(slotProps.data.id)"
                    />
                  </template>
                </Column>
              </DataTable>
            </TabPanel>
            <TabPanel
              value="2"
              class="w-full"
            >
              <div class="flex justify-between mb-4">
                <h3 class="text-lg font-medium">
                  Documents
                </h3>
                <Button
                  v-if="isEditing"
                  icon="pi pi-plus"
                  label="Add Document"
                />
              </div>
              <DataTable
                :value="service.documents || []"
                sort-field="name"
                scrollable
                scroll-height="500px"
                class="w-full"
              >
                <template #empty>
                  <div class="flex flex-wrap items-center justify-center py-8">
                    <div class="text-center">
                      <i class="pi pi-file text-5xl text-gray-300 mb-3" />
                      <p class="text-gray-500">
                        No documents have been added yet.
                      </p>
                    </div>
                  </div>
                </template>

                <!-- Columns -->
                <Column
                  field="name"
                  header="Name"
                  sortable
                />
                <Column
                  field="date"
                  header="Date"
                  sortable
                />
                <Column
                  field="type"
                  header="Type"
                  sortable
                />
                <Column>
                  <template #body="slotProps">
                    <Button
                      icon="pi pi-times"
                      severity="danger"
                      rounded
                      outlined
                      aria-label="Delete"
                      @click="deleteItem(slotProps.data.id)"
                    />
                  </template>
                </Column>
              </DataTable>
            </TabPanel>
            <TabPanel
              value="3"
              class="w-full"
            >
              <div class="flex justify-between mb-4">
                <h3 class="text-lg font-medium">
                  Service Providers
                </h3>
                <Button
                  v-if="isEditing"
                  icon="pi pi-plus"
                  label="Add Provider"
                />
              </div>
              <DataTable
                :value="service.providers || []"
                sort-field="name"
                scrollable
                scroll-height="500px"
                class="w-full"
              >
                <template #empty>
                  <div class="flex flex-wrap items-center justify-center py-8">
                    <div class="text-center">
                      <i class="pi pi-users text-5xl text-gray-300 mb-3" />
                      <p class="text-gray-500">
                        No service providers have been added yet.
                      </p>
                    </div>
                  </div>
                </template>

                <!-- Columns -->
                <Column
                  field="name"
                  header="Name"
                  sortable
                />
                <Column
                  field="type"
                  header="Type"
                  sortable
                />
                <Column
                  field="contactPerson"
                  header="Contact Person"
                />
                <Column
                  field="phone"
                  header="Phone Number"
                />
                <Column>
                  <template #body="slotProps">
                    <Button
                      icon="pi pi-eye"
                      class="p-button-rounded p-button-text p-button-sm"
                      @click="viewProvider(slotProps.data)"
                    />
                    <Button
                      v-if="isEditing"
                      icon="pi pi-times"
                      severity="danger"
                      rounded
                      outlined
                      aria-label="Delete"
                      @click="deleteProvider(slotProps.data.id)"
                    />
                  </template>
                </Column>
              </DataTable>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </div>
    </div>
  </ContactsLayout>
</template>

<script setup>
import { ref, watch, onMounted, computed, defineProps } from "vue";
import { useRoute, useRouter } from 'vue-router';
import { useServiceStore } from '@/stores/servicesStore';
import { useFakeDataStore } from '@/stores/fakeDataStore';
import { getFakeServiceById } from '@/services/fakeDataService';
import { useFormActions } from "@/composables/useFormActions";
import ContactsLayout from '@/layouts/ContactsLayout.vue';
import defaultImage from "@/assets/icons/Service.svg"; // Default image for services

const props = defineProps({
    data: { type: Object, default: () => ({}) },
    type: { type: String, default: 'view' },
    useFakeData: { type: Boolean, default: false }
});

const route = useRoute();
const router = useRouter();
const serviceStore = useServiceStore();
const fakeDataStore = useFakeDataStore();

const service = ref({ ...props.data });

// Use composable for managing form actions
const {
    isEditing,
    startEdit,
    cancelEdit,
    saveChanges,
    updateItem,
    triggerDelete,
    initializeForm,
} = useFormActions();

// Delete item from a list
const deleteItem = (id) => {
    // This would need to be implemented for each tab's data array
    console.log('Deleting item with ID:', id);
};

// View provider details
const viewProvider = (provider) => {
    if (provider && provider.id) {
        if (provider.type === 'Company') {
            router.push(`/view-company/${provider.id}`);
        } else {
            router.push(`/view-contact/${provider.id}`);
        }
    }
};

// Delete provider
const deleteProvider = (id) => {
    if (service.value.providers) {
        service.value.providers = service.value.providers.filter(provider => provider.id !== id);
    }
};

// Handle image upload using PrimeVue FileUpload component
const onImageUpload = (event) => {
    const file = event.files[0];
    if (file) {
        const reader = new FileReader();
        reader.onload = () => {
            service.value.image = reader.result; // Update service's image with the base64 result
        };
        reader.readAsDataURL(file);
    }
};

// Function to navigate back to the services list
const goBack = () => {
    router.push('/services');
};

// Use fake data for development/testing purposes
const useFakeData = true; // Set to true to use fake data, false to use real data

// Load service data on component mount
onMounted(async () => {
    initializeForm(props.type === 'add');

    if (props.type === 'view' && route.params.id) {
        try {
            const id = route.params.id;

            let serviceData;
            if (useFakeData || props.useFakeData) {
                // Use fake data store
                serviceData = fakeDataStore.setSelectedService(id);
                if (!serviceData) {
                    // If not found in cache, load it directly
                    serviceData = await getFakeServiceById(id);
                }
            } else if (serviceStore) {
                // Use regular service store
                serviceData = await serviceStore.getServiceById(parseInt(id));
            }

            if (serviceData) {
                service.value = { ...serviceData };
            } else {
                console.error('Service not found');
                // Optional: Display a message or redirect
            }
        } catch (error) {
            console.error('Error loading service:', error);
        }
    } else if (props.type === 'add') {
        // Initialize with empty data for a new service
        service.value = {
            name: "",
            type: "",
            pricePerHour: "",
            minimumHours: "",
            description: "",
            isBookable: false,
            isVat: false,
            image: defaultImage,
            pricing: [],
            documents: [],
            providers: []
        };
    }
});

// Watch for changes in the type prop to adjust the form state
watch(
    () => props.type,
    (newType) => {
        initializeForm(newType === "add");
    }
);

// Computed property to handle image errors with better fallback strategy
const serviceImageSrc = computed(() => {
    // If there's no image or if there was an error loading it, use the default
    if (!service.value.image || service.value.imageError) {
        return defaultImage;
    }

    // Check if the image is a URL
    if (typeof service.value.image === 'string' &&
        (service.value.image.startsWith('http://') ||
            service.value.image.startsWith('https://'))) {
        return service.value.image;
    }

    // Otherwise, use the default image
    return defaultImage;
});

// Handle image error
const handleImageError = () => {
    console.error('Error loading image:', service.value.image);
    // Set an error flag to prevent retrying the same broken image
    service.value.imageError = true;
};
</script>

<style scoped>
/* Ensure components take full width */
:deep(.p-tabview-panels) {
    width: 100% !important;
}

:deep(.p-tabview-panel) {
    width: 100% !important;
}

:deep(.p-datatable) {
    width: 100% !important;
}

/* Image container styling */
.image-container {
    min-width: 200px;
    max-width: 200px;
    min-height: 200px;
    max-height: 200px;
}

/* Ensure SVG icons display well within the container */
:deep(svg) {
    width: 80%;
    height: 80%;
    margin: auto;
}
</style>
